import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, map, of } from 'rxjs';

import { DisqualifyReasonsAutomationsActions } from '@core/disqualify-reasons-data';
import { JobWorkflowAutomationsActions } from '@core/offers-data';
import { JobTemplateWorkflowAutomationsActions } from '@recruitee/job-templates-data';
import { PipelineTemplateStageAutomationsActions } from '@recruitee/workflow-templates-data';

import { AutomationAccessService } from './automation-access.service';

@Injectable()
export class AutomationOptionsDataService {
  private readonly store = inject(Store);
  private readonly actions = inject(Actions);
  private readonly automationAccessService = inject(AutomationAccessService);

  public fetchOptions(data: {
    pipelineTemplateId?: number;
    disqualifyReasonId?: number;
    jobId?: number;
    jobTemplateId?: number;
  }) {
    const { jobId, jobTemplateId } = data;
    if (this.automationAccessService.triggersHaveUpsell && (jobId || jobTemplateId)) {
      return combineLatest([
        this.getOptionsForContext$(data),
        this.getOptionsForContext$({ jobId, jobTemplateId }),
      ]).pipe(
        map(([mainOptions, optionsForUpsell]) => {
          const allOptions = [
            ...mainOptions,
            ...optionsForUpsell.map(option => ({ ...option, isUpsell: true })),
          ];
          return allOptions;
        }),
      );
    }
    return this.getOptionsForContext$(data);
  }

  private getOptionsForContext$(data: {
    pipelineTemplateId?: number;
    disqualifyReasonId?: number;
    jobId?: number;
    jobTemplateId?: number;
  }) {
    const { pipelineTemplateId, disqualifyReasonId, jobId, jobTemplateId } = data;

    if (pipelineTemplateId) {
      return this.getPipelineTemplateOptions(pipelineTemplateId);
    }
    if (disqualifyReasonId) {
      return this.getDisqualifyReasonOptions(disqualifyReasonId);
    }
    if (jobTemplateId) {
      return this.getJobTemplateOptions(jobTemplateId);
    }
    if (jobId) {
      return this.getJobOptions(jobId);
    }
    return of([]);
  }

  private getPipelineTemplateOptions(id: number) {
    this.store.dispatch(
      PipelineTemplateStageAutomationsActions.fetchOptions({
        payload: { id },
      }),
    );
    return this.actions.pipe(
      ofType(PipelineTemplateStageAutomationsActions.fetchOptionsSuccess),
      map(data => data.payload),
    );
  }

  private getDisqualifyReasonOptions(id: number) {
    this.store.dispatch(
      DisqualifyReasonsAutomationsActions.fetchOptions({
        payload: { id },
      }),
    );
    return this.actions.pipe(
      ofType(DisqualifyReasonsAutomationsActions.fetchOptionsSuccess),
      map(data => data.payload),
    );
  }

  private getJobTemplateOptions(id: number) {
    this.store.dispatch(
      JobTemplateWorkflowAutomationsActions.fetchOptions({
        payload: { id },
      }),
    );
    return this.actions.pipe(
      ofType(JobTemplateWorkflowAutomationsActions.fetchOptionsSuccess),
      map(data => data.payload),
    );
  }

  private getJobOptions(id: number) {
    this.store.dispatch(
      JobWorkflowAutomationsActions.fetchOptions({
        payload: { id },
      }),
    );
    return this.actions.pipe(
      ofType(JobWorkflowAutomationsActions.fetchOptionsSuccess),
      map(data => data.payload),
    );
  }
}
